<script lang="ts">
import { defineComponent } from 'vue'
import OrderDataService from '@/services/OrderDataService'
import OrderTable from '@/components/OrderTable.vue'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import PageBreadcrumb from '@/components/common/PageBreadcrumb.vue'
import { TableDateRange } from '@/components/common/table'
import { Button } from '@/components/ui/button'
import { RefreshCw, X } from 'lucide-vue-next'

interface ProfitData {
    total_profit: number
    total_count: number
    current_month_profit: number
    prev_month_profit: number
}

export default defineComponent({
    name: 'OrderProfitView',
    components: {
        OrderTable,
        TableDateRange,
        Button,
        RefreshCw,
        X,
        PageBreadcrumb,
    },
    data() {
        return {
            loading: true,
            appliedDates: {
                start: '',
                end: '',
            },
            data: {
                total_profit: 0,
                total_count: 0,
                current_month_profit: 0,
                prev_month_profit: 0,
            } as ProfitData,
        }
    },
    computed: {
        bothDatesSet(): boolean {
            return !!(this.appliedDates.start && this.appliedDates.end)
        },
        isFiltered(): boolean {
            return !!(this.appliedDates.start && this.appliedDates.end)
        },
    },
    mounted() {
        this.loadProfit()
    },
    methods: {
        async loadProfit() {
            this.loading = true
            try {
                const filters = this.bothDatesSet
                    ? { start_date: this.appliedDates.start, end_date: this.appliedDates.end }
                    : undefined
                const response = await OrderDataService.getPaidOrdersProfit(filters)
                if (response.data.success) {
                    this.data = response.data.data
                }
            } catch (error) {
                console.error('Error loading profit data:', error)
                toast.error('Chyba pri načítavaní údajov o obrate')
            } finally {
                this.loading = false
            }
        },
        dateRangeChanged(range: { start: string; end: string } | null) {
            const start = range?.start ?? ''
            const end = range?.end ?? ''
            // A single bound is not a valid range - keep the previously applied range
            if ((start && !end) || (!start && end)) return
            this.appliedDates.start = start
            this.appliedDates.end = end
            this.loadProfit()
        },
        clearFilters() {
            this.appliedDates.start = ''
            this.appliedDates.end = ''
            this.loadProfit()
        },
        formatEur(value: number | undefined): string {
            if (value === undefined || value === null) return '0,00 €'
            return Number(value).toLocaleString('sk-SK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
        },
        formatDate(dateStr: string): string {
            if (!dateStr) return ''
            const [y, m, d] = dateStr.split('-')
            return `${d}.${m}.${y}`
        },
        getCurrentMonthName(): string {
            const months = ['Január', 'Február', 'Marec', 'Apríl', 'Máj', 'Jún', 'Júl', 'August', 'September', 'Október', 'November', 'December']
            return months[new Date().getMonth()]
        },
        getPreviousMonthName(): string {
            const months = ['Január', 'Február', 'Marec', 'Apríl', 'Máj', 'Jún', 'Júl', 'August', 'September', 'Október', 'November', 'December']
            const prev = new Date().getMonth() - 1
            return months[prev < 0 ? 11 : prev]
        },
    },
})
</script>

<template>
    <div class="px-4 sm:px-6 lg:px-8 py-8">
        <div class="sm:flex sm:items-center sm:justify-between">
            <div class="sm:flex-auto">
                <PageBreadcrumb page-title="Obrat z objednávok" />
                <p class="mt-2 text-sm text-gray-700 dark:text-gray-300">Prehľad obratu z uhradených objednávok</p>
            </div>
        </div>

        <div class="mt-6 flex flex-col sm:flex-row sm:items-center gap-3">
            <TableDateRange
                :model-value="{ start: appliedDates.start, end: appliedDates.end }"
                @update:model-value="dateRangeChanged"
            />
            <div class="flex items-center gap-2">
                <Button @click="loadProfit">
                    <RefreshCw />
                    <span>Obnoviť</span>
                </Button>
                <Button variant="outline" @click="clearFilters">
                    <X />
                    <span>Vymazať</span>
                </Button>
            </div>
        </div>

        <div v-if="loading" class="mt-8 flex justify-center">
            <div class="animate-spin rounded-full size-12 border-b-2 border-gray-900 dark:border-gray-600"></div>
        </div>

        <template v-else>
            <!-- Summary Cards -->
            <div class="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2" :class="isFiltered ? 'lg:grid-cols-2' : 'lg:grid-cols-4'">
                <!-- Total profit -->
                <div class="bg-white overflow-hidden shadow-xs rounded-lg dark:bg-white/3">
                    <div class="p-5">
                        <div class="flex items-center">
                            <div class="shrink-0">
                                <svg class="size-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div class="ml-5 w-0 flex-1">
                                <dl>
                                    <dt class="text-sm font-medium text-gray-500 truncate dark:text-gray-400">
                                        {{ isFiltered ? 'Obrat za vybrané obdobie' : 'Celkový obrat' }}
                                    </dt>
                                    <dd class="text-3xl font-semibold text-gray-900 dark:text-white/90">{{ formatEur(data.total_profit) }}</dd>
                                    <dd v-if="isFiltered" class="text-xs text-gray-500 mt-1 dark:text-gray-400">
                                        {{ formatDate(appliedDates.start) }} – {{ formatDate(appliedDates.end) }}
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Total count -->
                <div class="bg-white overflow-hidden shadow-xs rounded-lg dark:bg-white/3">
                    <div class="p-5">
                        <div class="flex items-center">
                            <div class="shrink-0">
                                <svg class="size-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div class="ml-5 w-0 flex-1">
                                <dl>
                                    <dt class="text-sm font-medium text-gray-500 truncate dark:text-gray-400">
                                        {{ isFiltered ? 'Počet objednávok za obdobie' : 'Počet uhradených objednávok' }}
                                    </dt>
                                    <dd class="text-3xl font-semibold text-gray-900 dark:text-white/90">{{ data.total_count }}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Current month (only when no date filter) -->
                <div v-if="!isFiltered" class="bg-white overflow-hidden shadow-xs rounded-lg dark:bg-white/3">
                    <div class="p-5">
                        <div class="flex items-center">
                            <div class="shrink-0">
                                <svg class="size-6 text-brand-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M8 7V3m8 4V3m-9 8h10m-9 4h6M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2-2v12a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <div class="ml-5 w-0 flex-1">
                                <dl>
                                    <dt class="text-sm font-medium text-gray-500 truncate dark:text-gray-400">Obrat tento mesiac</dt>
                                    <dd class="text-3xl font-semibold text-gray-900 dark:text-white/90">{{ formatEur(data.current_month_profit) }}</dd>
                                    <dd class="text-xs text-gray-500 mt-1 dark:text-gray-400">{{ getCurrentMonthName() }}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Previous month (only when no date filter) -->
                <div v-if="!isFiltered" class="bg-white overflow-hidden shadow-xs rounded-lg dark:bg-white/3">
                    <div class="p-5">
                        <div class="flex items-center">
                            <div class="shrink-0">
                                <svg class="size-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div class="ml-5 w-0 flex-1">
                                <dl>
                                    <dt class="text-sm font-medium text-gray-500 truncate dark:text-gray-400">Obrat minulý mesiac</dt>
                                    <dd class="text-3xl font-semibold text-gray-900 dark:text-white/90">{{ formatEur(data.prev_month_profit) }}</dd>
                                    <dd class="text-xs text-gray-500 mt-1 dark:text-gray-400">{{ getPreviousMonthName() }}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="mt-6">
                <OrderTable
                    title="Uhradené objednávky"
                    :isPaidOnly="true"
                    :filterStartDate="appliedDates.start"
                    :filterEndDate="appliedDates.end"
                />
            </div>
        </template>
    </div>
</template>
